/**
 * Comms (2026-11-04): what is wrong with the rig RIGHT NOW, and a record of what went wrong, kept across shows.
 *
 * Decided with the operator on `public/comms-study.html` (answers in audits/reports/2026-11-04-timeline-playback.md):
 * the OPBridge pill says it (amber or red, a red one flashes), the banner under the bar shows every problem, and the
 * Computers section keeps them: "Right now", a health line on each computer, and "Earlier", kept across shows "for
 * future dev work and pattern spotting". There is no separate section.
 *
 * Why it exists: a toast says a problem once and is gone, the pill knew only "connected or not", BRIDGE LOST only shows
 * when EVERY computer is gone, and the cues OPBridge reports as failed (`timeline_fired`, ok:false) were dropped.
 *
 * A problem is keyed by what it is about (`lost:<bridge>`, `slow:<bridge>`, `cues:<bridge>:<timeline>`,
 * `part:<timeline>:<bridge>`, `seek:<bridge>`, `offline:<bridge>`), raised when it starts and cleared when it ends; the history records each once, with
 * when it began and ended.
 */
import { createEffect, createRoot, createSignal, untrack } from 'solid-js';
import { bridges, connectionsVersion, getConnection } from './store.ts';
import { wtSupported } from './wtTransport.ts';
import { onTimelineFired } from './consumerWs.ts';
import { transportRoundTrips, timelineRunning } from '../timeline/transport.ts';
import { getTimeline, timelines } from '../timeline/store.ts';

export type CommsLevel = 'bad' | 'warn' | 'info';

export interface CommsIssue {
  key: string;
  level: CommsLevel;
  /** The computer it is about (a bridge id), or null for the rig as a whole. */
  bridgeId: string | null;
  /** Two or three words, for the pill: "Mac Studio lost", "3 cues failed". */
  short: string;
  /** One line: "Can't see Mac Studio". */
  title: string;
  /** What it means, in a sentence. */
  text: string;
  /** What to do, or '' when nothing needs doing. */
  todo: string;
  /** When it began (epoch ms). */
  since: number;
}

export interface CommsRecord {
  /** Unique: the problem's key and when it began. */
  id: string;
  at: number;
  level: CommsLevel;
  /** The computer's name at the time, or null. */
  computer: string | null;
  title: string;
  /** When it ended; absent while it lasts (or if the app closed first). */
  until?: number;
}

const HISTORY_KEY = 'opbuttons.comms.history.v1';
/** Enough for months of shows at a few problems each; the oldest go first. */
const HISTORY_MAX = 500;

function loadHistory(): CommsRecord[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const v = raw ? JSON.parse(raw) as unknown : [];
    return Array.isArray(v) ? (v as CommsRecord[]).filter((r) => r && typeof r.at === 'number' && typeof r.title === 'string') : [];
  } catch {
    return [];
  }
}
function saveHistory(list: CommsRecord[]): void {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(list)); } catch { /* storage full or blocked: the live view still works */ }
}

const [issueMap, setIssueMap] = createSignal<ReadonlyMap<string, CommsIssue>>(new Map());
const [history, setHistory] = createSignal<CommsRecord[]>(loadHistory());
/** The history record each live problem opened, by key. */
const openRecord = new Map<string, string>();

const LEVEL_ORDER: Record<CommsLevel, number> = { bad: 0, warn: 1, info: 2 };

/** What is wrong now, the worst first, then the newest. Reactive. */
export function commsIssues(): CommsIssue[] {
  return [...issueMap().values()].sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level] || b.since - a.since);
}

/** The problem with this key, if it is live. Reactive. */
export function commsIssue(key: string): CommsIssue | null {
  return issueMap().get(key) ?? null;
}

/** Everything that went wrong, newest first, kept across shows. Reactive. */
export function commsHistory(): CommsRecord[] {
  return history();
}

/** Start (or update) a problem. Its `since` is kept from when it began. */
export function raiseIssue(i: Omit<CommsIssue, 'since'>): void {
  const prev = issueMap().get(i.key);
  if (prev && prev.level === i.level && prev.short === i.short && prev.title === i.title && prev.text === i.text && prev.todo === i.todo) return;
  const now = Date.now();
  const next = new Map(issueMap());
  next.set(i.key, { ...i, since: prev?.since ?? now });
  setIssueMap(next);
  const name = i.bridgeId ? bridgeName(i.bridgeId) : null;
  if (!prev) {
    const rec: CommsRecord = { id: `${i.key}@${now}`, at: now, level: i.level, computer: name, title: i.title };
    openRecord.set(i.key, rec.id);
    const list = [rec, ...history()].slice(0, HISTORY_MAX);
    setHistory(list);
    saveHistory(list);
  } else {
    // The same problem growing (a third cue failing): its record says the latest.
    const id = openRecord.get(i.key);
    if (id) {
      const list = history().map((r) => (r.id === id ? { ...r, level: i.level, title: i.title } : r));
      setHistory(list);
      saveHistory(list);
    }
  }
}

/** End a problem: it leaves the live list and its record gets its end. */
export function clearIssue(key: string): void {
  if (!issueMap().has(key)) return;
  const next = new Map(issueMap());
  next.delete(key);
  setIssueMap(next);
  const id = openRecord.get(key);
  openRecord.delete(key);
  if (id) {
    const until = Date.now();
    const list = history().map((r) => (r.id === id ? { ...r, until } : r));
    setHistory(list);
    saveHistory(list);
  }
}

/** A problem that is an EVENT (a jump a computer missed): shown for `ms`, then it ends by itself. */
export function flashIssue(i: Omit<CommsIssue, 'since'>, ms = 30_000): void {
  raiseIssue(i);
  setTimeout(() => clearIssue(i.key), ms);
}

/** Every problem about one computer ends (it was unpaired or removed). */
function clearFor(bridgeId: string): void {
  for (const k of [...issueMap().keys()]) if (issueMap().get(k)?.bridgeId === bridgeId) clearIssue(k);
}

export function bridgeName(bridgeId: string): string {
  return bridges().find((b) => b.id === bridgeId)?.name?.trim() || 'a computer';
}

/** The running timelines this computer plays a part of (lazy: the parts compile only when asked). */
async function runningPartsOn(bridgeId: string): Promise<string[]> {
  const { timelineParts } = await import('../timeline/bridgeSync.ts');
  const out: string[] = [];
  for (const t of untrack(timelines)) {
    if (!timelineRunning(t.id)) continue;
    try { if (timelineParts(t).parts.some((p) => p.cfg.id === bridgeId)) out.push(t.name); } catch { /* a bad compile says nothing here */ }
  }
  return out;
}

/** A reading slower than this is a slow computer (ms; the LAN answers in a few). */
export const SLOW_MS = 1000;
/** And it is fine again under this. */
const FAST_AGAIN_MS = 500;
/** Readings in a row over `SLOW_MS` before it counts (one slow answer is a blip). */
const SLOW_READINGS = 2;
/** A computer's failed cues stay a problem until this long after the last one. */
const CUES_CLEAR_MS = 60_000;
/** An offline path with under this long left is a problem. Pre-flight's `WT_PIN_WARN_S`, the same number and reason:
 *  OPBridge re-mints a pin about 3 days before it ends, so a rig that is reached online never gets here. */
const OFFLINE_WARN_S = 48 * 3600;

let installed = false;

/** Start watching the rig: lost and slow computers, and cues OPBridge reports failed. Once, at boot. */
export function installComms(): void {
  if (installed) return;
  installed = true;
  createRoot(() => {
    // A computer lost: what the connection itself decides after its missed replies (three, `connection.ts`), never
    // one slow answer. Red when it plays a part of something running (that part may be playing, unseen), else amber.
    createEffect(() => {
      connectionsVersion();
      const paired = bridges().filter((b) => !!b.bearer);
      for (const b of paired) {
        const status = getConnection(b.id)?.statusSignal()() ?? 'unreachable';
        const key = `lost:${b.id}`;
        if (status === 'unreachable') {
          if (untrack(() => issueMap().has(key))) continue;
          const name = b.name?.trim() || 'a computer';
          raiseIssue({ key, level: 'warn', bridgeId: b.id, short: `${name} lost`, title: `Can't see ${name}`,
            text: 'Presses meant for it cannot reach it.',
            todo: 'It reconnects by itself when it can. If it has not within a minute: is OPBridge running on it, and is it on the network?' });
          void runningPartsOn(b.id).then((names) => {
            if (names.length === 0 || !issueMap().has(key)) return;
            raiseIssue({ key, level: 'bad', bridgeId: b.id, short: `${name} lost`, title: `Can't see ${name}`,
              text: `${names.join(', ')}: what it plays there may still be playing, and OPButtons can't tell. Presses meant for it cannot reach it.`,
              todo: 'It reconnects by itself when it can. If it has not within a minute: is OPBridge running on it, and is it on the network?' });
          });
        } else if (status === 'connected') {
          untrack(() => clearIssue(key));
        }
      }
      // A computer removed from the rig takes its problems with it.
      const ids = new Set(paired.map((b) => b.id));
      untrack(() => { for (const i of issueMap().values()) if (i.bridgeId && !ids.has(i.bridgeId)) clearFor(i.bridgeId); });
    });

    // A slow computer: its readings (the transport times each one) over SLOW_MS twice running; fine again under
    // FAST_AGAIN_MS. Amber: what it plays keeps its own clock, only what is shown of it lags.
    const overCount = new Map<string, number>();
    createEffect(() => {
      const rtts = transportRoundTrips();
      untrack(() => {
        for (const [id, ms] of Object.entries(rtts)) {
          const key = `slow:${id}`;
          if (ms > SLOW_MS) {
            const n = (overCount.get(id) ?? 0) + 1;
            overCount.set(id, n);
            if (n >= SLOW_READINGS) {
              const name = bridgeName(id);
              raiseIssue({ key, level: 'warn', bridgeId: id, short: `${name} slow`, title: `${name} is slow to answer`,
                text: `Its answers take ${(ms / 1000).toFixed(1)} s. What it plays keeps its own time; what you see of it here lags.`,
                todo: 'If it lasts, check its network: wired holds better than Wi-Fi.' });
            }
          } else {
            overCount.set(id, 0);
            if (ms < FAST_AGAIN_MS) clearIssue(key);
          }
        }
      });
    });

    // A computer's offline path running out (Pre-flight's "Offline path armed", said here too because a pin dies
    // BETWEEN shows, when nobody opens Pre-flight). Only for a computer that HAS one: a rig that never armed it is not
    // told so every day. Amber: nothing that plays now depends on it. Checked each minute and when a computer changes.
    const [minute, setMinute] = createSignal(0);
    setInterval(() => setMinute((n) => n + 1), 60_000);
    createEffect(() => {
      minute();
      const paired = bridges().filter((b) => !!b.bearer);
      untrack(() => {
        const nowS = Date.now() / 1000;
        for (const b of paired) {
          const key = `offline:${b.id}`;
          // The dialler's own horizon (`wtKey` in store.ts): the chain's end, else the current pin's.
          const h = wtSupported() && b.wt_url ? (b.wt_hashes_valid_until_unix_s ?? b.wt_expires_at_unix_s ?? null) : null;
          if (h === null || h - nowS >= OFFLINE_WARN_S) { clearIssue(key); continue; }
          const name = b.name?.trim() || 'a computer';
          const gone = h <= nowS;
          raiseIssue({ key, level: 'warn', bridgeId: b.id, short: `${name} offline path`,
            title: gone ? `${name}'s offline path has run out` : `${name}'s offline path runs out in ${Math.max(1, Math.round((h - nowS) / 3600))} h`,
            text: gone ? 'Where there is no internet, this computer cannot be found until it is renewed.'
              : 'Where there is no internet, this computer will not be found once it runs out.',
            todo: 'Connect to it once while it can be reached online: it renews by itself.' });
        }
      });
    });
  });

  // Cues OPBridge reports failed: one problem per computer and timeline, counting them, ended a minute after the
  // last. The same report can arrive twice (the primary's socket and a peer's), so a step is counted once a moment.
  const counts = new Map<string, number>();
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const seen = new Map<string, number>();
  onTimelineFired((evt) => {
    if (evt.ok || !evt.bridgeId) return;
    const dedupe = `${evt.bridgeId}|${evt.timelineId}|${evt.stepId}`;
    const now = Date.now();
    if ((seen.get(dedupe) ?? 0) > now - 2000) return;
    seen.set(dedupe, now);
    if (seen.size > 500) for (const [k, t] of seen) if (t < now - 10_000) seen.delete(k);
    const key = `cues:${evt.bridgeId}:${evt.timelineId}`;
    const n = (counts.get(key) ?? 0) + 1;
    counts.set(key, n);
    const name = bridgeName(evt.bridgeId);
    const tl = getTimeline(evt.timelineId)?.name ?? 'a timeline';
    raiseIssue({ key, level: 'bad', bridgeId: evt.bridgeId, short: `${n} cue${n === 1 ? '' : 's'} failed`,
      title: `${n} cue${n === 1 ? '' : 's'} failed on ${name}`,
      text: `In ${tl}: ${evt.error?.trim() || 'OPBridge did not say why'}.`,
      todo: 'Check the device is on and plugged in. The next cue tries again by itself; this ends a minute after the last failure.' });
    const old = timers.get(key);
    if (old) clearTimeout(old);
    timers.set(key, setTimeout(() => { counts.delete(key); timers.delete(key); clearIssue(key); }, CUES_CLEAR_MS));
  });
}

/** "12 s", "3 min", "2 h": how long ago, or how long, in the fewest words. */
export function spanText(ms: number): string {
  const s = Math.max(0, ms / 1000);
  if (s < 60) return `${Math.round(s)} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  return `${Math.round(s / 3600)} h`;
}
